import { isRecord } from "../types/data";
import type { DateValue } from "../types/data";

export interface UserAppearance {
  appBackgroundUrl?: string | null;
  appBackgroundUpdatedAt?: DateValue;
  profileBackgroundUrl?: string | null;
  profileBackgroundUpdatedAt?: DateValue;
}

const PROFILE_APPEARANCE_SECTIONS = new Set([
  "profile",
  "estimates",
  "results",
  "films",
  "collections",
  "game",
  "search",
  "friends",
  "achievements",
  "activity",
  "notifications",
  "sources",
  "import"
]);

const getBackgroundVersion = (value: unknown) => {
  if (!value) return null;

  if (isRecord(value) && typeof value.toMillis === "function") {
    const milliseconds: unknown = value.toMillis();
    return typeof milliseconds === "number" && Number.isFinite(milliseconds) ? milliseconds : null;
  }

  if (isRecord(value) && typeof value.seconds === "number" && Number.isFinite(value.seconds)) {
    const nanoseconds = typeof value.nanoseconds === "number" && Number.isFinite(value.nanoseconds) ? value.nanoseconds : 0;
    return value.seconds * 1000 + Math.floor(nanoseconds / 1000000);
  }

  const date = value instanceof Date ? value : typeof value === "string" || typeof value === "number" ? new Date(value) : new Date(NaN);
  const milliseconds = date.getTime();
  return Number.isNaN(milliseconds) ? null : milliseconds;
};

const appendBackgroundVersion = (url: string | null | undefined, updatedAt: unknown) => {
  const version = getBackgroundVersion(updatedAt);
  if (!url || version === null) return url || null;

  const hashIndex = url.indexOf("#");
  const baseUrl = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : url.slice(hashIndex);
  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}v=${version}${hash}`;
};

export const getProfileAppearanceUserId = (pathname: string, fallbackUserId: string | null = null) => {
  const [encodedUserId, section] = String(pathname || "")
    .split("/")
    .filter(Boolean);

  if (!encodedUserId || !PROFILE_APPEARANCE_SECTIONS.has(section)) {
    return fallbackUserId || null;
  }

  try {
    return decodeURIComponent(encodedUserId) || fallbackUserId || null;
  } catch {
    return encodedUserId || fallbackUserId || null;
  }
};

export const resolveUserBackgroundUrl = (
  userData: UserAppearance | null | undefined,
  type: "app" | "profile",
  fallbackUrl: string | null | undefined
) => {
  const backgroundUrl = userData?.[`${type}BackgroundUrl`];
  if (!backgroundUrl) return fallbackUrl;

  return appendBackgroundVersion(
    backgroundUrl,
    userData?.[`${type}BackgroundUpdatedAt`]
  );
};

export const resolveAppBackgroundForProfileRoute = ({
  appearanceUserId,
  authUserId,
  ownBackgroundUrl,
  viewedProfileAppearance,
  fallbackUrl
}: { appearanceUserId?: string | null; authUserId?: string | null; ownBackgroundUrl?: string | null; viewedProfileAppearance?: (UserAppearance & { userId?: string }) | null; fallbackUrl?: string | null } = {}) => {
  const isForeignProfile = Boolean(
    appearanceUserId && authUserId && appearanceUserId !== authUserId
  );
  if (!isForeignProfile) return ownBackgroundUrl || fallbackUrl || null;
  if (viewedProfileAppearance?.userId !== appearanceUserId) {
    return fallbackUrl || null;
  }
  return viewedProfileAppearance?.appBackgroundUrl || fallbackUrl || null;
};
